import React from 'react';
import {
  GraduationCap,
  Award,
  Briefcase,
  Calendar,
  ExternalLink,
  Building,
  BookOpen,
} from 'lucide-react';
import { SectionCard, InfoRow, TabLoading, TabEmpty, StatusBadge } from './shared';
import { useUserEducation, useUserCertifications, useUserWorkExperience } from '../../hooks/useUserTabs';
import { cn, formatDateTime } from '../../utils/helpers';
import type { UserEducation, UserCertification, UserWorkExperience } from '../../types/userTabs';

interface CredentialsTabProps {
  userId: number;
}

// Normalize API response: could be an array or { items: [...] }
function normalizeArray<T>(data: unknown): T[] {
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object' && 'items' in data && Array.isArray((data as any).items)) {
    return (data as any).items;
  }
  return [];
}

const CredentialsTab: React.FC<CredentialsTabProps> = ({ userId }) => {
  const education = useUserEducation(userId, true);
  const certifications = useUserCertifications(userId, true);
  const workExperience = useUserWorkExperience(userId, true);

  const eduList = normalizeArray<UserEducation>(education.data);
  const certList = normalizeArray<UserCertification>(certifications.data);
  const workList = normalizeArray<UserWorkExperience>(workExperience.data);

  const allLoading = education.loading && certifications.loading && workExperience.loading;
  const allEmpty =
    !education.loading && !certifications.loading && !workExperience.loading &&
    eduList.length === 0 && certList.length === 0 && workList.length === 0;

  if (allLoading) return <TabLoading />;
  if (allEmpty) return <TabEmpty message="No credentials on file" icon={GraduationCap} />;

  return (
    <div className="space-y-5">
      {/* Education */}
      <EducationSection
        data={eduList}
        loading={education.loading}
        error={education.error}
      />

      {/* Certifications */}
      <CertificationsSection
        data={certList}
        loading={certifications.loading}
        error={certifications.error}
      />

      {/* Work Experience */}
      <WorkExperienceSection
        data={workList}
        loading={workExperience.loading}
        error={workExperience.error}
      />
    </div>
  );
};

// ============================================================================
// Helper: format a date range
// ============================================================================

function formatDateRange(startDate: string | null, endDate: string | null, isCurrent?: boolean): string {
  const formatDate = (d: string) =>
    new Date(d).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });

  if (!startDate && !endDate) return '';
  if (startDate && !endDate) {
    return isCurrent ? `${formatDate(startDate)} - Present` : `Started ${formatDate(startDate)}`;
  }
  if (!startDate && endDate) return `Until ${formatDate(endDate)}`;
  return `${formatDate(startDate!)} - ${formatDate(endDate!)}`;
}

// ============================================================================
// Education Section
// ============================================================================

interface EducationSectionProps {
  data: UserEducation[] | null;
  loading: boolean;
  error: string | null;
}

const EducationSection: React.FC<EducationSectionProps> = ({ data, loading, error }) => {
  if (loading) {
    return (
      <SectionCard title="Education" icon={GraduationCap}>
        <div className="flex items-center justify-center py-8">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          <span className="ml-2.5 text-sm text-muted-foreground">Loading...</span>
        </div>
      </SectionCard>
    );
  }

  if (error) {
    return (
      <SectionCard title="Education" icon={GraduationCap}>
        <div className="flex flex-col items-center py-8 gap-2">
          <p className="text-sm text-red-400">{error}</p>
        </div>
      </SectionCard>
    );
  }

  if (!data || data.length === 0) {
    return (
      <SectionCard title="Education" icon={GraduationCap}>
        <div className="flex flex-col items-center justify-center py-8 gap-2">
          <GraduationCap className="h-6 w-6 text-muted-foreground/40" />
          <p className="text-sm text-muted-foreground">No education records</p>
        </div>
      </SectionCard>
    );
  }

  return (
    <SectionCard
      title="Education"
      icon={GraduationCap}
      subtitle={`${data.length} record${data.length !== 1 ? 's' : ''}`}
    >
      <div className="space-y-3">
        {data.map((edu) => {
          const dateRange = formatDateRange(edu.startDate, edu.endDate);

          return (
            <div
              key={edu.id}
              className="rounded-lg border border-border/50 bg-muted/5 p-4"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                  <div className="p-2 rounded-lg bg-blue-500/10 flex-shrink-0 mt-0.5">
                    <GraduationCap className="h-4 w-4 text-blue-400" />
                  </div>
                  <div className="min-w-0">
                    {edu.institution && (
                      <p className="text-sm font-medium text-foreground truncate">
                        {edu.institution}
                      </p>
                    )}
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      {edu.degree && (
                        <span className="text-xs text-muted-foreground">{edu.degree}</span>
                      )}
                      {edu.degree && edu.fieldOfStudy && (
                        <span className="text-muted-foreground/40">&middot;</span>
                      )}
                      {edu.fieldOfStudy && (
                        <span className="text-xs text-muted-foreground">{edu.fieldOfStudy}</span>
                      )}
                    </div>
                    {dateRange && (
                      <div className="flex items-center gap-1.5 mt-1.5">
                        <Calendar className="h-3 w-3 text-muted-foreground" />
                        <span className="text-[11px] text-muted-foreground">{dateRange}</span>
                      </div>
                    )}
                    {edu.description && (
                      <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
                        {edu.description}
                      </p>
                    )}
                  </div>
                </div>
                <span className="text-[10px] text-muted-foreground font-mono flex-shrink-0">
                  #{edu.id}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </SectionCard>
  );
};

// ============================================================================
// Certifications Section
// ============================================================================

interface CertificationsSectionProps {
  data: UserCertification[] | null;
  loading: boolean;
  error: string | null;
}

const CertificationsSection: React.FC<CertificationsSectionProps> = ({ data, loading, error }) => {
  if (loading) {
    return (
      <SectionCard title="Certifications" icon={Award}>
        <div className="flex items-center justify-center py-8">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          <span className="ml-2.5 text-sm text-muted-foreground">Loading...</span>
        </div>
      </SectionCard>
    );
  }

  if (error) {
    return (
      <SectionCard title="Certifications" icon={Award}>
        <div className="flex flex-col items-center py-8 gap-2">
          <p className="text-sm text-red-400">{error}</p>
        </div>
      </SectionCard>
    );
  }

  if (!data || data.length === 0) {
    return (
      <SectionCard title="Certifications" icon={Award}>
        <div className="flex flex-col items-center justify-center py-8 gap-2">
          <Award className="h-6 w-6 text-muted-foreground/40" />
          <p className="text-sm text-muted-foreground">No certifications</p>
        </div>
      </SectionCard>
    );
  }

  return (
    <SectionCard
      title="Certifications"
      icon={Award}
      subtitle={`${data.length} certification${data.length !== 1 ? 's' : ''}`}
    >
      <div className="space-y-3">
        {data.map((cert) => {
          const isExpired = cert.expirationDate && new Date(cert.expirationDate) < new Date();

          return (
            <div
              key={cert.id}
              className="rounded-lg border border-border/50 bg-muted/5 p-4"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                  <div className={cn(
                    'p-2 rounded-lg flex-shrink-0 mt-0.5',
                    isExpired ? 'bg-red-500/10' : 'bg-emerald-500/10'
                  )}>
                    <Award className={cn(
                      'h-4 w-4',
                      isExpired ? 'text-red-400' : 'text-emerald-400'
                    )} />
                  </div>
                  <div className="min-w-0">
                    {cert.name && (
                      <p className="text-sm font-medium text-foreground truncate">
                        {cert.name}
                      </p>
                    )}
                    {cert.issuingOrganization && (
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <Building className="h-3 w-3 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">{cert.issuingOrganization}</span>
                      </div>
                    )}

                    {/* Dates */}
                    <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                      {cert.issueDate && (
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3 text-muted-foreground" />
                          <span className="text-[11px] text-muted-foreground">
                            Issued {new Date(cert.issueDate).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                          </span>
                        </div>
                      )}
                      {cert.expirationDate && (
                        <span className={cn(
                          'text-[11px]',
                          isExpired ? 'text-red-400' : 'text-muted-foreground'
                        )}>
                          {isExpired ? 'Expired' : 'Expires'}{' '}
                          {new Date(cert.expirationDate).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                        </span>
                      )}
                    </div>

                    {/* Credential ID */}
                    {cert.credentialId && (
                      <div className="mt-2">
                        <span className="text-[10px] text-muted-foreground uppercase tracking-wider">ID: </span>
                        <span className="text-[11px] font-mono text-muted-foreground">{cert.credentialId}</span>
                      </div>
                    )}

                    {/* Credential URL */}
                    {cert.credentialUrl && (
                      <a
                        href={cert.credentialUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 mt-2 text-[11px] text-primary hover:text-primary/80 transition-colors"
                      >
                        <ExternalLink className="h-3 w-3" />
                        View Credential
                      </a>
                    )}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  {isExpired && <StatusBadge status="Expired" variant="error" />}
                  <span className="text-[10px] text-muted-foreground font-mono">#{cert.id}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </SectionCard>
  );
};

// ============================================================================
// Work Experience Section
// ============================================================================

interface WorkExperienceSectionProps {
  data: UserWorkExperience[] | null;
  loading: boolean;
  error: string | null;
}

const WorkExperienceSection: React.FC<WorkExperienceSectionProps> = ({ data, loading, error }) => {
  if (loading) {
    return (
      <SectionCard title="Work Experience" icon={Briefcase}>
        <div className="flex items-center justify-center py-8">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          <span className="ml-2.5 text-sm text-muted-foreground">Loading...</span>
        </div>
      </SectionCard>
    );
  }

  if (error) {
    return (
      <SectionCard title="Work Experience" icon={Briefcase}>
        <div className="flex flex-col items-center py-8 gap-2">
          <p className="text-sm text-red-400">{error}</p>
        </div>
      </SectionCard>
    );
  }

  if (!data || data.length === 0) {
    return (
      <SectionCard title="Work Experience" icon={Briefcase}>
        <div className="flex flex-col items-center justify-center py-8 gap-2">
          <Briefcase className="h-6 w-6 text-muted-foreground/40" />
          <p className="text-sm text-muted-foreground">No work experience</p>
        </div>
      </SectionCard>
    );
  }

  // Sort: current positions first, then by start date descending
  const sorted = [...data].sort((a, b) => {
    if (a.isCurrent && !b.isCurrent) return -1;
    if (!a.isCurrent && b.isCurrent) return 1;
    if (a.startDate && b.startDate) {
      return new Date(b.startDate).getTime() - new Date(a.startDate).getTime();
    }
    return 0;
  });

  return (
    <SectionCard
      title="Work Experience"
      icon={Briefcase}
      subtitle={`${data.length} position${data.length !== 1 ? 's' : ''}`}
    >
      <div className="space-y-3">
        {sorted.map((exp) => {
          const dateRange = formatDateRange(exp.startDate, exp.endDate, exp.isCurrent);

          return (
            <div
              key={exp.id}
              className={cn(
                'rounded-lg border bg-muted/5 p-4',
                exp.isCurrent ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-border/50'
              )}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                  <div className={cn(
                    'p-2 rounded-lg flex-shrink-0 mt-0.5',
                    exp.isCurrent ? 'bg-emerald-500/10' : 'bg-muted/30'
                  )}>
                    <Briefcase className={cn(
                      'h-4 w-4',
                      exp.isCurrent ? 'text-emerald-400' : 'text-muted-foreground'
                    )} />
                  </div>
                  <div className="min-w-0">
                    {exp.title && (
                      <p className="text-sm font-medium text-foreground truncate">
                        {exp.title}
                      </p>
                    )}
                    {exp.company && (
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <Building className="h-3 w-3 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">{exp.company}</span>
                      </div>
                    )}
                    {dateRange && (
                      <div className="flex items-center gap-1.5 mt-1.5">
                        <Calendar className="h-3 w-3 text-muted-foreground" />
                        <span className="text-[11px] text-muted-foreground">{dateRange}</span>
                      </div>
                    )}
                    {exp.description && (
                      <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
                        {exp.description}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  {exp.isCurrent && <StatusBadge status="Current" variant="success" />}
                  <span className="text-[10px] text-muted-foreground font-mono">#{exp.id}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </SectionCard>
  );
};

export default CredentialsTab;
